import { PublicationError } from '@streamlain/doc-publication-contract';
import { localAccountKeyError } from '@streamlain/view-config/local-data-seal';

import {
  LOCAL_INDEX_RUN_PART_PREFIX,
  type LocalIndexRow,
  type LocalIndexSegment,
} from '../../../local-data/payload';
import type { LocalAuthBinding } from '../../../storage/local-data-access';
import type { IDBConnection } from '../db';
import type { IDBEncryptionContext } from '../encryption';
import {
  type IndexSegmentCAS,
  type IndexSegmentChange,
  type IndexSegmentHead,
  type IndexSegmentStart,
  type IndexSegmentStore,
  sameIndexHead,
} from './segment-store';

const head = (state: IndexSegmentHead | undefined): IndexSegmentHead | null =>
  state ? { revision: state.revision, segmentId: state.segmentId } : null;
const isRecordRow = (
  row: LocalIndexRow | undefined
): row is LocalIndexSegment => row?.role === 'record';

/** Sealed index segments in the `indexSegments`/`indexSegmentMeta` stores. */
export class IDBIndexSegmentStore implements IndexSegmentStore<IDBEncryptionContext> {
  readonly binding: LocalAuthBinding;
  constructor(private readonly connection: IDBConnection) {
    const binding = connection.encryption.options.authBinding;
    connection.encryption.validate();
    if (!binding || connection.encryption.plain)
      throw localAccountKeyError('LOCKED');
    this.binding = binding;
  }
  get authorized() {
    return this.connection.encryption.authorized;
  }
  waitForConnected(signal: AbortSignal) {
    return this.connection.waitForConnected(signal);
  }
  onClosed(listener: () => void) {
    return this.connection.onStatusChanged(status => {
      if (status === 'closed' || status === 'error') listener();
    });
  }
  async readStart(
    validate: (start: IndexSegmentStart) => void,
    signal: AbortSignal
  ) {
    return this.connection.encryption.read(
      [
        'indexSegments',
        'indexSegmentMeta',
        'indexerRecords',
        'invertedIndex',
        'indexerMetadata',
      ],
      false,
      async (tx, context) => {
        // Account bindings never persist the plaintext indexer stores.
        for (const store of [
          'indexerRecords',
          'invertedIndex',
          'indexerMetadata',
        ] as const)
          if (await tx.objectStore(store).count())
            throw localAccountKeyError('CORRUPT');
        const state = head(
          await tx.objectStore('indexSegmentMeta').get('state')
        );
        const meta = state
          ? await tx.objectStore('indexSegments').get(state.segmentId)
          : undefined;
        if (meta && meta.role !== 'metadata')
          throw localAccountKeyError('CORRUPT');
        const start: IndexSegmentStart = {
          state,
          count: await tx.objectStore('indexSegments').count(),
          meta: meta ?? null,
        };
        validate(start);
        if (start.meta) await context.capture(tx, start.meta.keyScopeId, false);
        return start;
      },
      signal
    );
  }
  async readRecordTables(
    expected: IndexSegmentHead | null,
    signal: AbortSignal
  ) {
    return this.connection.encryption.transaction(
      ['indexSegmentMeta', 'indexSegments'],
      'readonly',
      async tx => {
        const state = await tx.objectStore('indexSegmentMeta').get('state');
        if (!sameIndexHead(state, expected))
          throw new PublicationError('LOCAL_CONFLICT');
        const segments = tx.objectStore('indexSegments');
        // Key cursors read index entries only, never the sealed payloads.
        const tables = new Map<string, string>();
        let keys = await segments.index('recordKey').openKeyCursor();
        while (keys) {
          tables.set(keys.primaryKey, keys.key[0]);
          keys = await keys.continue();
        }
        const live = new Map<number, string>();
        let nids = await segments
          .index('nid')
          .openKeyCursor(IDBKeyRange.lowerBound(0, true));
        while (nids) {
          const table = tables.get(nids.primaryKey);
          if (table === undefined) throw localAccountKeyError('CORRUPT');
          live.set(nids.key, table);
          nids = await nids.continue();
        }
        if (live.size !== tables.size) throw localAccountKeyError('CORRUPT');
        return live;
      },
      signal
    );
  }
  async readRows(
    expected: IndexSegmentHead | null,
    keys: readonly (number | string)[],
    signal: AbortSignal
  ) {
    return this.connection.encryption.read(
      ['indexSegments', 'indexSegmentMeta'],
      false,
      async (tx, context) => {
        const state = await tx.objectStore('indexSegmentMeta').get('state');
        if (!sameIndexHead(state, expected))
          throw new PublicationError('LOCAL_CONFLICT');
        const segments = tx.objectStore('indexSegments');
        const rows = await Promise.all(
          keys.map(key =>
            typeof key === 'number'
              ? segments.index('nid').get(key)
              : segments.get(key)
          )
        );
        for (const row of rows)
          if (row) await context.capture(tx, row.keyScopeId, false);
        return rows;
      },
      signal
    );
  }
  async readRunParts(expected: IndexSegmentHead | null, signal: AbortSignal) {
    return this.connection.encryption.read(
      ['indexSegments', 'indexSegmentMeta'],
      false,
      async (tx, context) => {
        const state = await tx.objectStore('indexSegmentMeta').get('state');
        if (!sameIndexHead(state, expected))
          throw new PublicationError('LOCAL_CONFLICT');
        // Run part ids share a prefix: one key range, no record payload read.
        const rows = await tx
          .objectStore('indexSegments')
          .getAll(
            IDBKeyRange.bound(
              LOCAL_INDEX_RUN_PART_PREFIX,
              `${LOCAL_INDEX_RUN_PART_PREFIX}\uffff`
            )
          );
        for (const scope of new Set(rows.map(row => row.keyScopeId)))
          await context.capture(tx, scope, false);
        return rows;
      },
      signal
    );
  }
  async readRoutes(
    expected: IndexSegmentHead | null,
    keys: readonly ([string, string] | number)[],
    signal: AbortSignal
  ) {
    return this.connection.encryption.transaction(
      ['indexSegmentMeta', 'indexSegments'],
      'readonly',
      async tx => {
        const state = await tx.objectStore('indexSegmentMeta').get('state');
        if (!sameIndexHead(state, expected))
          throw new PublicationError('LOCAL_CONFLICT');
        const segments = tx.objectStore('indexSegments');
        const rows = await Promise.all(
          keys.map(key =>
            typeof key === 'number'
              ? segments.index('nid').get(key)
              : segments.index('recordKey').get(key)
          )
        );
        return rows.map(row => {
          if (row && !isRecordRow(row)) throw localAccountKeyError('CORRUPT');
          return row;
        });
      },
      signal
    );
  }
  async readSnapshot(signal?: AbortSignal) {
    return this.connection.encryption.transaction(
      ['indexSegmentMeta', 'indexSegments'],
      'readonly',
      async tx => ({
        state: head(await tx.objectStore('indexSegmentMeta').get('state')),
        count: await tx.objectStore('indexSegments').count(),
      }),
      signal
    );
  }
  async discard(expected: IndexSegmentHead | null, signal: AbortSignal) {
    await this.connection.encryption.transaction(
      ['indexSegmentMeta', 'indexSegments'],
      'readwrite',
      async tx => {
        const state = await tx.objectStore('indexSegmentMeta').get('state');
        if (!sameIndexHead(state, expected))
          throw new PublicationError('LOCAL_CONFLICT');
        await tx.objectStore('indexSegments').clear();
        await tx.objectStore('indexSegmentMeta').clear();
      },
      signal
    );
  }
  assertRead(context: IDBEncryptionContext) {
    return this.connection.encryption.assertRead(context);
  }
  async assertSnapshot(
    context: IDBEncryptionContext,
    expected: IndexSegmentHead | null
  ) {
    const state = await this.connection.encryption.assertReadWith(
      context,
      ['indexSegmentMeta'],
      async tx => head(await tx.objectStore('indexSegmentMeta').get('state'))
    );
    if (!sameIndexHead(state, expected))
      throw new PublicationError('LOCAL_CONFLICT');
  }
  write<T>(work: () => Promise<T>) {
    return this.connection.encryption.write(work);
  }
  async prepareWrite(
    expected: IndexSegmentHead | null,
    rows: readonly IndexSegmentCAS[],
    scopes: readonly string[],
    signal: AbortSignal
  ) {
    const prepared = await this.connection.encryption.read(
      ['indexSegments', 'indexSegmentMeta'],
      true,
      async (tx, context) => {
        const state = await tx.objectStore('indexSegmentMeta').get('state');
        if (!sameIndexHead(state, expected))
          throw new PublicationError('LOCAL_CONFLICT');
        for (const row of rows) {
          const stored = await tx
            .objectStore('indexSegments')
            .get(row.segmentId);
          if ((stored?.storageRevision ?? null) !== row.expected)
            throw new PublicationError('LOCAL_CONFLICT');
        }
        for (const scope of scopes) await context.capture(tx, scope, true);
      },
      signal
    );
    return prepared.context;
  }
  async commitWrite(
    context: IDBEncryptionContext,
    expected: IndexSegmentHead | null,
    change: IndexSegmentChange,
    signal: AbortSignal
  ) {
    await this.connection.encryption.commit(
      context,
      ['indexSegments', 'indexSegmentMeta'],
      async tx => {
        const state = await tx.objectStore('indexSegmentMeta').get('state');
        if (!sameIndexHead(state, expected))
          throw new PublicationError('LOCAL_CONFLICT');
        const segments = tx.objectStore('indexSegments');
        for (const cas of [
          ...change.deletes,
          ...change.puts.map(({ row, expected }) => ({
            segmentId: row.segmentId,
            expected,
          })),
        ]) {
          const stored = await segments.get(cas.segmentId);
          if ((stored?.storageRevision ?? null) !== cas.expected)
            throw new PublicationError('LOCAL_CONFLICT');
        }
        for (const { segmentId } of change.deletes)
          await segments.delete(segmentId);
        for (const { row } of change.puts) await segments.put(row);
        await tx
          .objectStore('indexSegmentMeta')
          .put({ key: 'state', ...change.next });
      },
      signal
    );
  }
}
